/**
 * COTI-642 — Utilidades de "proceso origen del material" en el frontend.
 *
 * Enfoque actual: cada fila de material de un proceso puede indicar de qué proceso del
 * elemento proviene (columna "Proceso" de la grilla, habilitada por el flag
 * `RelProcesoElementoTipoCategoriaMaterial.ProcesoPrecedente`). Un material con proceso
 * origen no se costea (ni cantidad ni costo).
 *
 * Durante la edición el vínculo se guarda en la fila como `ProcesoOrigenUid` (`_uid` cliente,
 * identidad estable que sobrevive a reordenamientos). Al guardar se convierte a la posición
 * 1-based del proceso en el elemento (`ItemProcesoOrigen`), y al hidratar se convierte de
 * vuelta.
 *
 * Las funciones trabajan sobre el array PLANO de procesos del elemento, en orden global
 * (todas las secciones concatenadas en orden).
 */

/** `Proceso.Codigo` del proceso de Extrusión. */
export const CODIGO_PROCESO_EXTRUSION = 30;

/** Id cliente estable, no persistido. */
export function generarUidProceso() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Asegura que cada proceso tenga un `_uid` ÚNICO (in-place). Reasigna cuando el `_uid` ya lo
 * usa una fila anterior (p. ej. al duplicar un proceso o copiar el objeto con spread).
 */
export function asegurarUidsProcesos(procesos) {
  const vistos = new Set();
  (procesos || []).forEach((proceso) => {
    if (!proceso) return;
    if (!proceso._uid || vistos.has(proceso._uid)) {
      proceso._uid = generarUidProceso();
    }
    vistos.add(proceso._uid);
  });
  return procesos;
}

/** Aplana las secciones (`[{ ElementoProceso: [] }]`) a un array plano en orden global. */
export function aplanarSecciones(secciones) {
  return (secciones || []).flatMap((seccion) =>
    Array.isArray(seccion?.ElementoProceso) ? seccion.ElementoProceso : [],
  );
}

/** Todas las filas de material de un proceso (categorías + lista directa), sin duplicar refs. */
function filasMaterialDeProceso(proceso) {
  const filas = [];
  const vistas = new Set();
  const agregar = (lista) => {
    (lista || []).forEach((fila) => {
      if (fila && !vistas.has(fila)) {
        vistas.add(fila);
        filas.push(fila);
      }
    });
  };
  (proceso?.CategoriasMaterial || []).forEach((cat) => agregar(cat?.ElementoMateriales));
  agregar(proceso?.ElementoMateriales);
  return filas;
}

/**
 * Lista aplanada para el selector de "Proceso" de la grilla y para el diagrama:
 * `{ _uid, ordenGlobal, descripcion, seccion, esExtrusion, esInicial, codigoMaterialResultante,
 *    descripcionMaterialResultante, procesosOrigen }`.
 * `procesosOrigen` = `_uid`s de los procesos referenciados por las filas de material de este
 * proceso. `ordenGlobal` es la posición 1-based en el array plano.
 * `codigoMaterialResultante` = código del material que el proceso genera (típicamente una
 * Extrusión), o `null` si no genera ninguno — COTI-642 (49677): un proceso con material
 * resultante concreto sólo puede ser origen de una fila cuyo material sea EXACTAMENTE ese.
 * `descripcionMaterialResultante` = descripción de ese material si viene incluida (sólo para
 * el mensaje al usuario), o `null`.
 *
 * PURA. Usa `proceso._uid` tal cual: `asegurarUidsProcesos` (llamado por input_procesos.vue en
 * un watch imperativo, y también por podar/hidratación) garantiza que sea único. La identidad
 * que se ofrece en el selector es EXACTAMENTE la que validan esas funciones.
 */
export function aplanarProcesosParaPrecedentes(procesos) {
  return (procesos || []).map((proceso, indice) => {
    const procesosOrigen = [
      ...new Set(
        filasMaterialDeProceso(proceso)
          .map((fila) => fila?.ProcesoOrigenUid)
          .filter((valor) => typeof valor === 'string' && valor.length > 0),
      ),
    ];
    return {
      _uid: proceso?._uid ?? `pos-${indice + 1}`,
      ordenGlobal: indice + 1,
      descripcion: proceso?.Proceso?.Descripcion ?? proceso?.Descripcion ?? `Proceso ${indice + 1}`,
      seccion: proceso?.Seccion?.Descripcion ?? proceso?.SeccionDescripcion ?? null,
      esExtrusion: proceso?.Proceso?.Codigo === CODIGO_PROCESO_EXTRUSION,
      esInicial: proceso?.EsProcesoInicial === true,
      codigoMaterialResultante:
        proceso?.CodigoMaterialResultante ?? proceso?.MaterialResultante?.Codigo ?? null,
      descripcionMaterialResultante:
        proceso?.MaterialResultante?.DescripcionInterna ??
        proceso?.MaterialResultante?.Descripcion ??
        null,
      procesosOrigen,
    };
  });
}

/**
 * COTI-642 (49677): ¿el proceso `procesoPlano` (de `aplanarProcesosParaPrecedentes`) NO puede
 * ser el origen de una fila de material de código `codigoMaterialFila`?
 *
 * Sólo bloquea cuando el proceso genera un material resultante concreto
 * (`codigoMaterialResultante` no nulo) y ese código NO es igual al de la fila. Comparación por
 * código exacto — no por descripción, categoría, gramaje ni otras características. Si el proceso
 * no genera material, o la fila todavía no tiene material elegido, devuelve `false` (no bloquea).
 */
export function procesoOrigenIncompatibleConMaterial(procesoPlano, codigoMaterialFila) {
  const codigoResultante = procesoPlano?.codigoMaterialResultante;
  if (codigoResultante == null) return false;
  if (codigoMaterialFila == null) return false;
  return codigoResultante !== codigoMaterialFila;
}

/**
 * El proceso de menor orden global (el primero del array plano) es SIEMPRE inicial (raíz del
 * flujo): se fuerza `EsProcesoInicial = true` in-place. Los demás conservan lo que haya elegido
 * el usuario. Devuelve `true` si modificó algo.
 */
export function asegurarProcesoInicial(procesos) {
  const lista = (procesos || []).filter(Boolean);
  if (lista.length === 0) return false;
  if (lista[0].EsProcesoInicial === true) return false;
  lista[0].EsProcesoInicial = true;
  return true;
}

/** `Map<_uid, posición 1-based>` del array plano de procesos (asegura `_uid` primero). */
export function mapaPosicionPorUidProceso(procesos) {
  asegurarUidsProcesos(procesos);
  return new Map((procesos || []).map((proceso, i) => [proceso._uid, i + 1]));
}

/**
 * Hidratación: asigna `_uid` a cada proceso y convierte, en cada fila de material,
 * `ItemProcesoOrigen` (posición 1-based del backend) a `ProcesoOrigenUid` (`_uid` cliente).
 * Idempotente: si `ProcesoOrigenUid` ya es un `_uid` conocido se deja como está. Poda las
 * referencias inválidas (posición inexistente, o al propio proceso).
 */
export function materialesOrigenDesdeItemProceso(procesos) {
  asegurarUidsProcesos(procesos);
  const uidPorPosicion = new Map((procesos || []).map((proceso, i) => [i + 1, proceso._uid]));
  const uidsConocidos = new Set((procesos || []).map((proceso) => proceso._uid));

  (procesos || []).forEach((proceso) => {
    filasMaterialDeProceso(proceso).forEach((fila) => {
      const actual = fila.ProcesoOrigenUid;
      if (typeof actual === 'string' && uidsConocidos.has(actual) && actual !== proceso._uid) {
        return; // ya convertido y válido
      }
      const posicion = Number(fila.ItemProcesoOrigen);
      const uid = Number.isInteger(posicion) ? uidPorPosicion.get(posicion) : null;
      fila.ProcesoOrigenUid = uid && uid !== proceso._uid ? uid : null;
    });
  });

  return procesos;
}

/**
 * Poda de las filas de material los `ProcesoOrigenUid` que ya no son válidos (proceso
 * borrado / reordenado, o el del propio proceso). Devuelve `true` si modificó algo.
 */
export function podarProcesoOrigenInvalido(procesos) {
  asegurarUidsProcesos(procesos);
  const uidsValidos = new Set((procesos || []).map((proceso) => proceso._uid));
  let cambio = false;
  (procesos || []).forEach((proceso) => {
    filasMaterialDeProceso(proceso).forEach((fila) => {
      const uid = fila.ProcesoOrigenUid;
      if (uid != null && (!uidsValidos.has(uid) || uid === proceso._uid)) {
        fila.ProcesoOrigenUid = null;
        cambio = true;
      }
    });
  });
  return cambio;
}

/** ¿La categoría de material del proceso valida el ancho del item? */
function categoriaValidaAncho(proceso) {
  return (proceso?.CategoriasMaterial || []).some(
    (catMat) =>
      catMat?.ValidaAnchoMaterial === true ||
      catMat?.CategoriaMaterial?.ValidaAnchoMaterial === true ||
      catMat?.MaterialCategoria?.ValidaAnchoMaterial === true,
  );
}

/** ¿El proceso ya tiene cargado un material propio con ancho > 0? */
function tieneMaterialPropioConAncho(proceso) {
  return (proceso?.CategoriasMaterial || []).some((catMat) =>
    (catMat?.ElementoMateriales || []).some(
      (em) => Number(em?.Ancho) > 0 || Number(em?.Material?.Ancho) > 0,
    ),
  );
}

/** Ancho (mm) de la primera bobina del proceso (orden por `ItemBobina`). */
function anchoPrimeraBobina(proceso) {
  const bobinas = [...(proceso?.ElementoProcesoBobinas || [])].sort(
    (a, b) => (a?.ItemBobina ?? 0) - (b?.ItemBobina ?? 0),
  );
  return Number(bobinas[0]?.Ancho) || 0;
}

/**
 * Índices (en el array plano `procesos`) de los precedentes de `procesos[idx]`. Misma regla
 * que usa el diagrama de flujo: unión de los procesos origen de sus materiales (si los
 * declara) MÁS el proceso inmediatamente anterior por orden, salvo que este proceso esté
 * marcado como inicial (raíz del flujo) — en ese caso el anterior por orden no cuenta, pero
 * los orígenes de material sí se conservan.
 */
export function indicesPrecedentesDeProceso(procesos, idx) {
  const proceso = procesos?.[idx];
  if (!proceso) return [];

  const indicePorUid = new Map((procesos || []).map((p, i) => [p._uid, i]));
  const origenes = [
    ...new Set(
      filasMaterialDeProceso(proceso)
        .map((fila) => indicePorUid.get(fila?.ProcesoOrigenUid))
        .filter((i) => Number.isInteger(i) && i !== idx),
    ),
  ];

  const precedentes = new Set(origenes);
  if (proceso.EsProcesoInicial !== true && idx > 0) {
    precedentes.add(idx - 1);
  }
  return [...precedentes];
}

/**
 * COTI-642 — Ancho del item cuando el proceso que valida el ancho consume el material de
 * otro proceso (típicamente Extrusión) y no tiene material propio con ancho.
 *
 * Proceso objetivo = el primero (por orden) cuya categoría de material tiene
 * `ValidaAnchoMaterial`. Desde ahí camina hacia atrás hasta una Extrusión con bobinas,
 * siguiendo los precedentes de cada proceso (`indicesPrecedentesDeProceso`): los procesos
 * origen de sus materiales MÁS el anterior por orden, salvo que el proceso esté marcado como
 * inicial (ahí el anterior por orden no cuenta, aunque sí los orígenes de material).
 * Devuelve el ancho de la primera bobina de esa Extrusión (mm), o 0.
 *
 * @param {Array} secciones `elemento.Secciones`
 * @returns {number}
 */
export function resolverAnchoItemDesdeExtrusionPrecedente(secciones) {
  const procesos = aplanarSecciones(secciones);
  if (procesos.length === 0) return 0;

  asegurarUidsProcesos(procesos);

  const indiceValidaAncho = procesos.findIndex(
    (p) => categoriaValidaAncho(p) && !tieneMaterialPropioConAncho(p),
  );
  if (indiceValidaAncho === -1) return 0;

  const visitados = new Set();
  const pila = [indiceValidaAncho];

  while (pila.length > 0) {
    const idx = pila.pop();
    if (idx == null || idx < 0 || idx >= procesos.length || visitados.has(idx)) continue;
    visitados.add(idx);

    const proceso = procesos[idx];
    if (proceso?.Proceso?.Codigo === CODIGO_PROCESO_EXTRUSION && idx !== indiceValidaAncho) {
      const ancho = anchoPrimeraBobina(proceso);
      if (ancho > 0) return ancho;
    }

    // Precedentes: los procesos origen de los materiales de este proceso, o —si no declara
    // ninguno y no es un proceso inicial (raíz del flujo)— el anterior por orden.
    indicesPrecedentesDeProceso(procesos, idx).forEach((i) => pila.push(i));
  }

  return 0;
}
